import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogFooter,
  Field,
  Input,
  Row,
  Stack,
} from '@leanin/ui';
import { useTranslations } from 'use-intl';
import { normalizeLinkUrl } from './link-url';

// Link entry dialog for RichTextEditor (toolbar Link button and Cmd/Ctrl+K).
// Pure form UI: the editor owns the selection snapshot and applies the
// result — this component only collects text + URL and validates the URL
// against the dialect's http/https-only rule. Mounted per open (state
// initializes from props), so the caller renders it conditionally.

type Props = {
  /** Selection text (or the full existing link text) to prefill. */
  readonly initialText: string;
  /** Existing link href when editing; empty string when adding. */
  readonly initialUrl: string;
  /** True when opened on an existing link — shows the Remove action. */
  readonly isEditing: boolean;
  /** Called with the final display text and the normalized http/https URL. */
  readonly onSave: (text: string, url: string) => void;
  readonly onRemove: () => void;
  readonly onClose: () => void;
};

export function LinkDialog({
  initialText,
  initialUrl,
  isEditing,
  onSave,
  onRemove,
  onClose,
}: Props) {
  const t = useTranslations('Editor');
  const [text, setText] = useState(initialText);
  const [url, setUrl] = useState(initialUrl);
  const [urlError, setUrlError] = useState<string | null>(null);

  const submit = () => {
    const normalized = normalizeLinkUrl(url);
    if (normalized === null) {
      setUrlError(t('linkInvalid'));
      return;
    }
    const label = text.trim();
    onSave(label.length > 0 ? label : normalized, normalized);
  };

  const submitOnEnter = (e: React.KeyboardEvent) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    submit();
  };

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      size="sm"
      title={isEditing ? t('linkDialogEditTitle') : t('linkDialogAddTitle')}
      // Land in the URL field, not the first-in-DOM text field: the display
      // text is usually prefilled from the selection, so the URL is the one
      // thing the member came here to type. Selected on edit so an existing
      // href replaces in one keystroke.
      onOpenAutoFocus={(event) => {
        event.preventDefault();
        const content = event.currentTarget as HTMLElement | null;
        const urlInput = content?.querySelector<HTMLInputElement>('input[inputmode="url"]');
        urlInput?.focus();
        urlInput?.select();
      }}
    >
      <Stack gap="md">
        <Field label={t('linkTextLabel')}>
          <Input
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={submitOnEnter}
          />
        </Field>
        <Field label={t('linkUrlLabel')} error={urlError} required>
          <Input
            value={url}
            onChange={(e) => {
              setUrl(e.target.value);
              setUrlError(null);
            }}
            onKeyDown={submitOnEnter}
            placeholder={t('linkUrlPlaceholder')}
            inputMode="url"
          />
        </Field>
      </Stack>
      <DialogFooter justify={isEditing ? 'space-between' : 'flex-end'}>
        {isEditing ? (
          <Button variant="ghostDanger" onClick={onRemove}>
            {t('linkRemove')}
          </Button>
        ) : null}
        <Row gap="sm" align="center">
          <Button variant="ghost" onClick={onClose}>
            {t('linkCancel')}
          </Button>
          <Button variant="primary" onClick={submit}>
            {t('linkSave')}
          </Button>
        </Row>
      </DialogFooter>
    </Dialog>
  );
}
